/**
 * ParcelDetailSection — parcel research zoom (CT-020, Phase 2 — Round 22 inline editing).
 *
 * Renders into #aj-sc-sections when wf.zoomSection starts with 'parcel-detail:'.
 * Children-only render (ZoomTitle shows the APN) per the section-zoom rule.
 *
 * Outline-first: every field is an outline row; the two research categories and
 * their subsections are collapsible wf-node--section nodes. Formula/lookup fields
 * stay READ-ONLY and are tagged with a muted "derived" pill — they are computed by
 * Airtable (lot slope, acreage, coverage, conformance) and never recomputed here.
 *
 * Every other field is a genuine editable scalar on the Parcel record — those get
 * an ⋯ action menu (Edit) opening a small modal input, same idiom as
 * ConceptLevelAreaFields.jsx's ScopeFieldEditModal. Scope: PARCEL-owned scalars
 * only. Zone/Jurisdiction/Special Overlay/Owner/Association are link fields (need
 * a picker, not a text input) — as of 2026-09-29 those rows carry an ⋯ "Edit in Research Tool"
 * (the full-screen tool is THE input tool, Matt's call) and city-wide rows an "Edit in
 * Permitting"; the view refetches on `parcel-research-changed` when the tool closes. Zone/
 * Jurisdiction/Agency fields are SHARED
 * records requiring the confirm-before-edit warning already enforced by
 * services/parcels.js's updateZoneField/updateAgency — both stay out of scope
 * here and remain the full "Parcel Research" tool's job. The exact set of
 * editable-vs-computed fields mirrors duplicateParcel()'s COPY_SCALARS list in
 * services/parcels.js, which already had to make this same distinction.
 *
 * Lazy-load: on first view of a parcel it loads detail → historical permits →
 * jurisdiction agencies into ctx caches and dispatches outline-render.
 */
import { useEffect, useReducer, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { wf, saveViewState } from '../outline/state.js'
import { ctx } from '../outline/ctx.js'
import { getParcelDetail, getHistoricalPermits, getJurisdictionAgencies, updateParcelField, getParcelHoaAnswer, hoaStatusFor, zoneDisplayName } from '../services/parcels.js'
import { useActionMenu, ActionMenu, ActionMenuButton, ActionMenuItem } from './ActionMenu.jsx'
import Markdown from './Markdown.jsx'
import { loadReferenceDocuments } from './ReferenceDocumentsSection.jsx'
import { openReferenceDocument } from '../services/referenceDocuments.js'

const fmtDate = s => {
  if (!s) return ''
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s)
  if (!m) return s
  return `${m[2]}/${m[3]}/${m[1]}`
}

const has = v => v != null && v !== '' && !(Array.isArray(v) && v.length === 0)
const show = v => {
  if (v === true) return 'Yes'
  if (v === false) return 'No'
  if (Array.isArray(v)) return v.filter(x => x != null).map(show).join(', ') || '—'
  // Airtable returns {specialValue:'NaN'} / {error:'#ERROR!'} for uncomputable formula fields —
  // never render the raw object (React error #31).
  if (v && typeof v === 'object') { const sv = v.specialValue; return sv != null && sv !== 'NaN' ? String(sv) : '—' }
  return has(v) ? String(v) : '—'
}

/**
 * Format a percent-bearing field for display, rounded to the nearest hundredth.
 * `alreadyScaled` — true for fields whose formula already outputs a 0–100 number
 * (e.g. Allowable Lot Coverage Percent); false (default) for Airtable's native
 * `percent`-type fields, which store/return a 0–1 fraction (Lot Slope, Existing
 * Lot Coverage, LB R One Required Landscaped Open Space Percent, Required Open
 * Space Percent) and need ×100 before rounding.
 */
function showPct(v, alreadyScaled = false) {
  if (!has(v) || typeof v === 'object') return show(v)
  const n = Number(v) * (alreadyScaled ? 1 : 100)
  if (isNaN(n)) return '—'
  return (Math.round(n * 100) / 100) + '%'
}

function rerender() { window.dispatchEvent(new CustomEvent('outline-render')) }
function toggle(key) {
  wf.collapsed[key] = (wf.collapsed[key] === false) // collapsed default; flip
  saveViewState()
  rerender()
}

/**
 * A collapsible subsection node (outline grid). `meta` is optional trailing count text.
 * `zoomKey` — when set, the bullet becomes a real zoom target (data-kbzoom + click →
 * ctx.zoomToSection), same as every other zoomable outline node; omit for a plain
 * expand-only subsection (unchanged default behavior).
 */
function Sub({ collapseKey, label, meta, zoomKey, actions, children }) {
  const collapsed = wf.collapsed[collapseKey] !== false
  const menu = useActionMenu()
  function onZoom(e) {
    e.stopPropagation()
    if (zoomKey) ctx.zoomToSection(zoomKey, { root: 'design' })
  }
  return (
    <div className={`wf-node wf-node--section${collapsed ? ' wf-node--collapsed' : ''}`} data-key={collapseKey}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={() => toggle(collapseKey)}>
          <span className={`wf-tri-icon ${collapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button
          className="wf-bullet wf-bullet--section"
          style={{ cursor: zoomKey ? 'pointer' : 'default' }}
          onClick={onZoom}
          {...(zoomKey ? { 'data-kbzoom': '1', title: 'Focus on ' + label } : {})}
        ></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--section">{label}</span>
          {meta != null && <span className="wf-prog" style={{ color: 'var(--aj-text-muted)', marginLeft: '8px' }}>{meta}</span>}
        </div>
        {actions && actions.length > 0 && <ActionMenuButton menu={menu} />}
      </div>
      {actions && actions.length > 0 && (
        <ActionMenu menu={menu}>
          {actions.map(a => <ActionMenuItem key={a.label} onClick={() => { menu.close(); a.run() }}>{a.label}</ActionMenuItem>)}
        </ActionMenu>
      )}
      {!collapsed && <div className="wf-children">{children}</div>}
    </div>
  )
}

/**
 * Exact Airtable singleSelect option lists for parcel fields that read like Yes/No or category
 * text but are actually constrained selects — confirmed against the live Parcels schema
 * (tblBcOe6dw3wSlave). Free-text input on these would risk typecast:true silently creating a
 * new, misspelled option instead of matching an existing one.
 */
const SELECT_OPTIONS = {
  'Coastal Zone': ['Not in Coastal Zone', 'Coastal Zone (State)', 'Coastal Zone (Local Program)', 'Coastal Zone Appealable (Local Program)'],
  'Oceanfront Lot': ['Yes', 'No'],
  'FHSZ': ['Very High', 'High', 'Moderate', 'Not in Fire Hazard Severity Zone'],
  'FHSZ Responsibility': ['CA - State Responsibility Area (SRA)', 'CA - Local responsibility Area (LRA)', 'NV - Wildland Development Areas (WUI)', 'Not Applicable'],
  'Flood Zone or Water Course': ['Yes', 'No', 'Unknown'],
  'Landslide Zone': ['Yes', 'No', 'Unknown'],
  'Liquefaction': ['Yes', 'No', 'Unknown'],
  'Fault Lines': ['Yes', 'No'],
  'Allowed Lot Use': ['Single Family Residence', 'Multi Family Residence', 'Commercial Retail', 'Commercial Hospitality', 'Commercial Dining', 'Commercial Offices'],
  'Existing Lot Use': ['Single Family Residence', 'Multi Family Residence', 'Commercial Retail', 'Commercial Hospitality', 'Commercial Dining', 'Commercial Offices'],
  'LB Site Slope Configuration': ['Rear Lot Line Above Street', 'Through Lot', 'Rear Lot Line Below Street'],
  'Front Curb': ['Yes', 'No'],
}

/**
 * One label/value field row. `derived` marks an Airtable-computed value (never editable).
 * `editField` — the Airtable field name to PATCH when set (and not `derived`); adds a ⋯ Edit
 * action. `editType` — 'text' (default) | 'number' | 'checkbox' | 'textarea' | 'select'
 * (auto-applied when `editField` has an entry in SELECT_OPTIONS). `rawValue` — override for
 * what the edit modal loads as its starting value, for rows whose displayed `value` is a
 * formatted/fallback composite of the raw field (e.g. Lot Size shows the computed "Lot Size
 * Complete" string but edits the raw "Lot Size SF" number).
 */
function Field({ label, value, derived, link, editField, editType = 'text', rawValue, onEdit, tool }) {
  const menu = useActionMenu()
  const editable = !derived && !!editField
  // `tool` — { label, run } for a row entered somewhere else (a picker in the Research Tool, or a
  // city-wide value in Permitting): the ⋯ menu jumps there instead of offering a bare input.
  const jump = !editable && tool
  const choices = editField && SELECT_OPTIONS[editField]
  const resolvedType = choices ? 'select' : editType
  return (
    <div className="wf-node wf-node--item">
      <div className="wf-row">
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <span className="wf-bullet wf-bullet--item"></span>
        <div className="wf-text-wrap wf-pd-field">
          <span className="wf-pd-label">{label}</span>
          {link && has(value)
            ? <a className="wf-pd-value wf-pd-link" href={value} target="_blank" rel="noopener noreferrer">Open ↗</a>
            : <span className="wf-pd-value">{show(value)}</span>}
          {derived && <span className="wf-pd-derived">derived</span>}
        </div>
        {(editable || jump) && <ActionMenuButton menu={menu} />}
      </div>
      {editable && (
        <ActionMenu menu={menu}>
          <ActionMenuItem onClick={() => { menu.close(); onEdit({ label, editField, editType: resolvedType, choices, rawValue: rawValue !== undefined ? rawValue : value }) }}>Edit</ActionMenuItem>
        </ActionMenu>
      )}
      {jump && (
        <ActionMenu menu={menu}>
          <ActionMenuItem onClick={() => { menu.close(); tool.run() }}>{tool.label}</ActionMenuItem>
        </ActionMenu>
      )}
    </div>
  )
}

/** Standalone ⋯ Edit trigger for the Notes row, which renders Markdown rather than a plain
    value so it can't reuse Field's own ActionMenu wiring directly. */
function NotesMenu({ onEdit }) {
  const menu = useActionMenu()
  return (
    <>
      <ActionMenuButton menu={menu} />
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={() => { menu.close(); onEdit() }}>Edit</ActionMenuItem>
      </ActionMenu>
    </>
  )
}

/* ── ParcelFieldEditModal — single-field edit form (text/number/checkbox/textarea/select) ──────
   Mirrors ConceptLevelAreaFields.jsx's ScopeFieldEditModal. */
function ParcelFieldEditModal({ editing, onSave, onClose }) {
  const { label, editType, rawValue, choices } = editing
  const [draft, setDraft] = useState(() => {
    if (editType === 'checkbox') return !!rawValue
    // 'percent' fields store a 0–1 fraction in Airtable; edit them on the natural 0–100 scale.
    if (editType === 'percent') return rawValue == null ? '' : Math.round(Number(rawValue) * 100 * 100) / 100
    return rawValue == null ? '' : rawValue
  })
  const [saving, setSaving] = useState(false)

  async function handleSave() {
    setSaving(true)
    try {
      let next = draft
      if (editType === 'number') next = draft === '' ? null : Number(draft)
      else if (editType === 'percent') next = draft === '' ? null : Number(draft) / 100
      else if (editType === 'checkbox') next = !!draft
      else next = (typeof draft === 'string' ? draft.trim() : draft) || null
      await onSave(next)
      onClose()
    } catch {
      setSaving(false)
    }
  }

  const textInp = {
    fontFamily: '"DM Sans",sans-serif', fontSize: 12,
    padding: '7px 10px', border: '0.5px solid var(--aj-border-mid)',
    borderRadius: 4, background: 'var(--aj-bg-subtle)', color: 'var(--aj-text)',
    outline: 'none', width: '100%', boxSizing: 'border-box',
  }
  const numInp = { ...textInp, fontFamily: '"DM Mono",monospace', textAlign: 'right', width: 140 }

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 900, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: 'var(--aj-bg-elevated)', borderRadius: 10, padding: '28px 32px', width: 400, maxWidth: '90vw', display: 'flex', flexDirection: 'column', gap: 18 }} onClick={e => e.stopPropagation()}>
        <div style={{ fontFamily: 'Montserrat,sans-serif', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--aj-text)' }}>
          Edit {label}
        </div>
        {editType === 'number' && (
          <input autoFocus type="number" value={draft} onChange={e => setDraft(e.target.value)} style={numInp} />
        )}
        {editType === 'percent' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input autoFocus type="number" value={draft} onChange={e => setDraft(e.target.value)} style={numInp} />
            <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 11, color: 'var(--aj-text-mid)' }}>%</span>
          </div>
        )}
        {editType === 'checkbox' && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text)' }}>
            <input type="checkbox" checked={!!draft} onChange={e => setDraft(e.target.checked)} style={{ accentColor: '#07272D' }} />
            {draft ? 'Yes' : 'No'}
          </label>
        )}
        {editType === 'textarea' && (
          <textarea autoFocus value={draft} onChange={e => setDraft(e.target.value)} rows={6} style={{ ...textInp, resize: 'vertical', fontFamily: '"DM Sans",sans-serif' }} />
        )}
        {editType === 'text' && (
          <input autoFocus type="text" value={draft} onChange={e => setDraft(e.target.value)} style={textInp} />
        )}
        {editType === 'select' && (
          <select autoFocus value={draft || ''} onChange={e => setDraft(e.target.value)} style={{ ...textInp, cursor: 'pointer' }}>
            <option value="">—</option>
            {(choices || []).map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 8, borderTop: '0.5px solid var(--aj-border)', marginTop: 4 }}>
          <button onClick={onClose} style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, padding: '7px 16px', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, background: 'transparent', color: 'var(--aj-text)', cursor: 'pointer' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, padding: '7px 16px', border: 'none', borderRadius: 4, background: '#07272D', color: '#fff', cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

function calcVis() {
  return (wf.zoomSection || '').startsWith('parcel-detail:') ? 'zoom' : 'hidden'
}

/** Zoom host — renders the shared parcel content when zoomed into a parcel; otherwise nothing.
 *  Branches to the Agencies sub-zoom (parcel-detail:<id>:agencies) when present. */
export default function ParcelDetailSection() {
  const [, forceUpdate] = useReducer(v => v + 1, 0)
  useEffect(() => {
    const onRender = () => forceUpdate()
    window.addEventListener('outline-render', onRender)
    return () => window.removeEventListener('outline-render', onRender)
  }, [])
  if (calcVis() !== 'zoom') return null
  const parts = wf.zoomSection.split(':')
  const parcelId = parts[1] || ''
  if (parts[2] === 'agencies') return <ParcelAgenciesContent parcelId={parcelId} />
  return <ParcelDetailContent parcelId={parcelId} />
}

/**
 * ParcelAgenciesContent — section-zoom content for parcel-detail:<id>:agencies (ZoomTitle shows
 * the header; children only, per the section-zoom rule). Self-contained lazy-load so a direct
 * deep-link/refresh onto this zoom still loads the parcel + jurisdiction + agencies chain, same
 * as ParcelDetailContent does for the main zoom.
 */
function ParcelAgenciesContent({ parcelId }) {
  const [, forceUpdate] = useReducer(v => v + 1, 0)
  const loadedFor = useRef(null)

  useEffect(() => {
    const onRender = () => forceUpdate()
    window.addEventListener('outline-render', onRender)
    return () => window.removeEventListener('outline-render', onRender)
  }, [])

  useEffect(() => {
    if (!parcelId || loadedFor.current === parcelId) return
    loadedFor.current = parcelId
    ;(async () => {
      let detail = ctx.parcelDetailCache[parcelId]
      if (!detail || !detail.parcel) {
        detail = await getParcelDetail(parcelId)
        ctx.parcelDetailCache[parcelId] = detail
        rerender()
      }
      const jurisId = detail.jurisdiction && detail.jurisdiction.id
      if (jurisId && !ctx.agencyCache[jurisId]) {
        const orgs = await getJurisdictionAgencies(jurisId)
        ctx.agencyCache[jurisId] = orgs
        rerender()
      }
    })()
  }, [parcelId])

  const detail = ctx.parcelDetailCache[parcelId]
  if (!detail || !detail.parcel) return <div className="wf-list"><div className="wf-loading">Loading…</div></div>
  const jurisId = detail.jurisdiction && detail.jurisdiction.id
  const agencies = jurisId ? ctx.agencyCache[jurisId] : null

  return (
    <div className="wf-list">
      {agencies == null && <div className="wf-loading">Loading agencies…</div>}
      {Array.isArray(agencies) && agencies.length === 0 && (
        <div className="wf-loading" style={{ color: '#7a9aaa' }}>No agencies on file for this jurisdiction.</div>
      )}
      {Array.isArray(agencies) && agencies.map(o => (
        <div className="wf-node wf-node--item" data-key={'agency:' + o.id} key={o.id}>
          <div className="wf-row">
            <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
            <span className="wf-bullet wf-bullet--item"></span>
            <div className="wf-text-wrap wf-pd-field">
              <span className="wf-pd-label">{o.fields['Organization Name'] || 'Agency'}</span>
              <span className="wf-pd-value">{[o.fields['Agency Type'], o.fields['Phone'], o.fields['General Email']].filter(has).join(' · ') || '—'}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

/**
 * ParcelDetailContent — the parcel research branch (Permit Research + Development Standards).
 * Used both in the parcel-detail zoom (via ParcelDetailSection) and inline under an expanded
 * parcel row in the outline (via DesignSection's ParcelRow). Self-contained lazy-load keyed on
 * parcelId, so it loads whenever it mounts (expand or zoom).
 */
export function ParcelDetailContent({ parcelId }) {
  const [, forceUpdate] = useReducer(v => v + 1, 0)
  const loadedFor = useRef(null)
  const [editing, setEditing] = useState(null) // { label, editField, editType, rawValue } | null

  async function handleSaveField(nextValue) {
    const { editField } = editing
    await updateParcelField(parcelId, { [editField]: nextValue })
    const cached = ctx.parcelDetailCache[parcelId]
    if (cached && cached.parcel) cached.parcel.fields[editField] = nextValue
    rerender()
  }

  useEffect(() => {
    const onRender = () => forceUpdate()
    window.addEventListener('outline-render', onRender)
    const onPermits = e => {
      if (e.detail && e.detail.parcelId === parcelId) {
        getHistoricalPermits(parcelId).then(rows => { ctx.historicalPermitCache[parcelId] = rows; forceUpdate() })
      }
    }
    window.addEventListener('historical-permits-changed', onPermits)
    // The Research Tool is where parcel research is entered; refetch when it closes.
    const onResearch = e => {
      if (!e.detail || e.detail.parcelId !== parcelId) return
      Promise.all([getParcelDetail(parcelId), getParcelHoaAnswer(parcelId).catch(() => null), getHistoricalPermits(parcelId)])
        .then(([d, a, rows]) => {
          d.hoaAnswer = a
          ctx.parcelDetailCache[parcelId] = d
          ctx.historicalPermitCache[parcelId] = rows
          const jid = d.jurisdiction && d.jurisdiction.id
          if (jid && !ctx.agencyCache[jid]) getJurisdictionAgencies(jid).then(orgs => { ctx.agencyCache[jid] = orgs; rerender() })
          rerender()
        }).catch(() => {})
    }
    window.addEventListener('parcel-research-changed', onResearch)
    return () => {
      window.removeEventListener('outline-render', onRender)
      window.removeEventListener('historical-permits-changed', onPermits)
      window.removeEventListener('parcel-research-changed', onResearch)
    }
  }, [parcelId])

  // Lazy-load this parcel's detail + permits + agencies once.
  useEffect(() => {
    if (!parcelId || loadedFor.current === parcelId) return
    loadedFor.current = parcelId
    ;(async () => {
      const detail = await getParcelDetail(parcelId)
      ctx.parcelDetailCache[parcelId] = detail
      // Seed the category/subsection nodes open on first view (persists toggles after).
      ;['cat:permit', 'cat:dev', 'property', 'zoning', 'hazards', 'notes', 'surveys',
        'requirements', 'metrics', 'approvals', 'jurisdiction'].forEach(k => {
        const key = `parcel:${parcelId}:${k}`
        if (wf.collapsed[key] === undefined) wf.collapsed[key] = false
      })
      rerender()
      getParcelHoaAnswer(parcelId).then(a => { detail.hoaAnswer = a; rerender() }).catch(() => {})
      getHistoricalPermits(parcelId).then(rows => { ctx.historicalPermitCache[parcelId] = rows; rerender() })
      const jurisId = detail.jurisdiction && detail.jurisdiction.id
      if (jurisId && !ctx.agencyCache[jurisId]) {
        getJurisdictionAgencies(jurisId).then(orgs => { ctx.agencyCache[jurisId] = orgs; rerender() })
      }
    })()
  }, [parcelId])

  const detail = ctx.parcelDetailCache[parcelId]
  if (!detail || !detail.parcel) return <div className="wf-list"><div className="wf-loading">Loading parcel…</div></div>

  const f = detail.parcel.fields
  const zone = detail.zone ? detail.zone.fields : {}
  const juris = detail.jurisdiction ? detail.jurisdiction.fields : {}
  const k = sub => `parcel:${parcelId}:${sub}`

  const zoneName = zone['Name of Zone'] || zone['Name'] || f['Zone String'] || '—'
  const jurisName = juris['Name'] || '—'
  const lbShow = f['Show LB R One'] === 'Yes' || f['Show LB TAB Overlay'] === 'Yes'
    || /laguna beach/i.test(jurisName) || /laguna beach/i.test(String(f['Project City (from Project)'] || ''))

  const permits = ctx.historicalPermitCache[parcelId]
  // Survey files live in the project's Reference Documents; the parcel shows the same files
  // (kit items marked show_on_parcel), never a copy. docs/REFERENCE_DOCUMENTS.md.
  const projectId = wf.zoomProjectId || ctx.recordId || null
  const refDocs = projectId && ctx.refDocCache ? ctx.refDocCache[projectId] : undefined
  if (projectId && refDocs === undefined) loadReferenceDocuments(projectId)
  const surveyDocs = refDocs && refDocs.documents ? refDocs.documents.filter(d => d.show_on_parcel) : null
  const agencies = detail.jurisdiction ? ctx.agencyCache[detail.jurisdiction.id] : null

  const catPermit = wf.collapsed[k('cat:permit')] !== false
  const catDev = wf.collapsed[k('cat:dev')] !== false

  // Rows with no inline input are entered in the Research Tool (links) or Permitting (city-wide).
  const inTool = nav => ({ label: 'Edit in Research Tool', run: () => window.dispatchEvent(new CustomEvent('open-parcel-research-tool', { detail: { parcelId, nav } })) })
  const inPermitting = detail.jurisdiction
    ? { label: 'Edit in Permitting Hub', run: () => window.dispatchEvent(new CustomEvent('open-permitting', { detail: { tool: 'zones', jurisdictionId: detail.jurisdiction.id } })) }
    : inTool('zoning')
  const first = v => (Array.isArray(v) ? v[0] : v) || null
  const gisUrl = first(juris['Online GIS Parcel Search']) || first(f['Online GIS Parcel Search'])
  const codeUrl = first(juris['Online Municipal Code']) || first(f['Online Municipal Code (from Jurisdiction) (from Zone)'])
  const ownerName = [].concat(f['Owner'] || []).map(id => (detail.ownerNames || {})[id]).filter(Boolean).join(', ')
  const overlayName = (detail.overlays || []).map(o => zoneDisplayName(o.fields)).filter(Boolean).join(', ')

  const DEPTS = [
    ['Planning', 'Planning Department'],
    ['Building', 'Building Department'],
    ['Fire', 'Fire Department'],
    ['Health', 'Health Department'],
  ]

  return (
    <div className="wf-list wf-parcel-detail">

      {/* ── Category: Permit Research ─────────────────────────────── */}
      <div className={`wf-node wf-node--section${catPermit ? ' wf-node--collapsed' : ''}`} data-key={k('cat:permit')}>
        <div className="wf-row">
          <button className="wf-triangle" onClick={() => toggle(k('cat:permit'))}>
            <span className={`wf-tri-icon ${catPermit ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
          </button>
          <span className="wf-bullet wf-bullet--section"></span>
          <div className="wf-text-wrap"><span className="wf-node-text wf-node-text--section">Permit Research</span></div>
        </div>
        {!catPermit && (
          <div className="wf-children">
            <Sub collapseKey={k('property')} label="Property">
              <Field label="Property Owner" value={ownerName} tool={inTool('property')} />
              <Field label="APN" value={f['APN']} editField="APN" onEdit={setEditing} />
              <Field label="Legal Description" value={f['Legal Description']} editField="Legal Description" onEdit={setEditing} />
              <Field label="Lot Size" value={has(f['Lot Size SF']) ? (f['Lot Size Complete'] || f['Lot Size SF']) : null} editField="Lot Size SF" editType="number" rawValue={f['Lot Size SF']} onEdit={setEditing} />
              <Field label="GIS Link" value={gisUrl} link tool={inPermitting} />
            </Sub>
            <Sub collapseKey={k('zoning')} label="Zoning">
              <Field label="Zone" value={zoneName} tool={inTool('zoning')} />
              <Field label="Special Overlay" value={overlayName} tool={inTool('zoning')} />
              <Field label="HOA" value={{ has: 'Has HOA', none: 'No HOA', unknown: 'Not researched' }[hoaStatusFor(f, detail.hoaAnswer)]} tool={inTool('zoning')} />
              <Field label="Jurisdiction" value={jurisName} tool={inTool('zoning')} />
              <Field label="Allowed Lot Use" value={f['Allowed Lot Use']} editField="Allowed Lot Use" onEdit={setEditing} />
              <Field label="Existing Lot Use" value={f['Existing Lot Use']} editField="Existing Lot Use" onEdit={setEditing} />
            </Sub>
            <Sub collapseKey={k('hazards')} label="Hazards">
              <Field label="Coastal Zone" value={f['Coastal Zone']} editField="Coastal Zone" onEdit={setEditing} />
              <Field label="Oceanfront Lot" value={f['Oceanfront Lot']} editField="Oceanfront Lot" onEdit={setEditing} />
              <Field label="FHSZ" value={f['FHSZ']} editField="FHSZ" onEdit={setEditing} />
              <Field label="FHSZ Responsibility" value={f['FHSZ Responsibility Text'] || f['FHSZ Responsibility']} editField="FHSZ Responsibility" rawValue={f['FHSZ Responsibility']} onEdit={setEditing} />
              <Field label="Flood Zone or Water Course" value={f['Flood Zone or Water Course']} editField="Flood Zone or Water Course" onEdit={setEditing} />
              <Field label="Landslide Zone" value={f['Landslide Zone']} editField="Landslide Zone" onEdit={setEditing} />
              <Field label="Liquefaction" value={f['Liquefaction']} editField="Liquefaction" onEdit={setEditing} />
              <Field label="Fault Lines" value={f['Fault Lines']} editField="Fault Lines" onEdit={setEditing} />
            </Sub>
            <Sub collapseKey={k('notes')} label="Notes">
              <div className="wf-node wf-node--item">
                <div className="wf-row">
                  <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
                  <span className="wf-bullet wf-bullet--item"></span>
                  <div className="wf-text-wrap">
                    {has(f['Project Specific Permitting Notes'])
                      ? <Markdown text={String(f['Project Specific Permitting Notes'])} />
                      : <span className="wf-pd-value" style={{ fontStyle: 'italic', color: 'var(--aj-text-muted)' }}>—</span>}
                  </div>
                  <NotesMenu onEdit={() => setEditing({ label: 'Notes', editField: 'Project Specific Permitting Notes', editType: 'textarea', rawValue: f['Project Specific Permitting Notes'] })} />
                </div>
              </div>
            </Sub>
          </div>
        )}
      </div>

      {/* ── Category: Development Standards ────────────────────────── */}
      <div className={`wf-node wf-node--section${catDev ? ' wf-node--collapsed' : ''}`} data-key={k('cat:dev')}>
        <div className="wf-row">
          <button className="wf-triangle" onClick={() => toggle(k('cat:dev'))}>
            <span className={`wf-tri-icon ${catDev ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
          </button>
          <span className="wf-bullet wf-bullet--section"></span>
          <div className="wf-text-wrap"><span className="wf-node-text wf-node-text--section">Development Standards</span></div>
        </div>
        {!catDev && (
          <div className="wf-children">
            <Sub collapseKey={k('requirements')} label="Requirements">
              <Field label="Required Front Yard Setback" value={f['Required Front Yard Setback']} editField="Required Front Yard Setback" editType="number" onEdit={setEditing} />
              <Field label="Required Rear Yard Setback" value={f['Required Rear Yard Setback']} editField="Required Rear Yard Setback" editType="number" onEdit={setEditing} />
              <Field label="Required Side Yard Setback 1" value={f['Required Side Yard Setback 1']} editField="Required Side Yard Setback 1" editType="number" onEdit={setEditing} />
              <Field label="Required Side Yard Setback 2" value={f['Required Side Yard Setback 2']} editField="Required Side Yard Setback 2" editType="number" onEdit={setEditing} />
              <Field label="Required Covered Parking Spaces" value={f['Required Covered Parking Spaces']} editField="Required Covered Parking Spaces" editType="number" onEdit={setEditing} />
              <Field label="Required Uncovered Parking Spaces" value={f['Required Uncovered Parking Spaces']} editField="Required Uncovered Parking Spaces" editType="number" onEdit={setEditing} />
              <Field label="Required Open Space Percent" value={showPct(f['Required Open Space Percent'])} editField="Required Open Space Percent" editType="percent" rawValue={f['Required Open Space Percent']} onEdit={setEditing} />
              <Field label="Required Open Space SF" value={f['Required Open Space SF']} derived />
              <Field label="Allowable Height from Grade" value={f['Allowable Height from Grade Manual']} editField="Allowable Height from Grade Manual" editType="number" onEdit={setEditing} />
              <Field label="Allowable Height from Rear Lot Line" value={f['Allowable Height from Rear Lot Line Manual']} editField="Allowable Height from Rear Lot Line Manual" editType="number" onEdit={setEditing} />
              <Field label="Allowable Height from Front Lot Line" value={f['Allowable Height from Front Lot Line Manual']} editField="Allowable Height from Front Lot Line Manual" editType="number" onEdit={setEditing} />
            </Sub>

            {lbShow && (
              <Sub collapseKey={k('metrics')} label="Existing Metrics (Laguna Beach)">
                <Field label="Average Lot Depth" value={f['Average Lot Depth']} editField="Average Lot Depth" editType="number" onEdit={setEditing} />
                <Field label="Average Lot Width" value={f['Average Lot Width']} editField="Average Lot Width" editType="number" onEdit={setEditing} />
                <Field label="Elevation — High Side Corner 1" value={f['Lot High Corner 1 Elevation']} editField="Lot High Corner 1 Elevation" editType="number" onEdit={setEditing} />
                <Field label="Elevation — High Side Corner 2" value={f['Lot High Corner 2 Elevation']} editField="Lot High Corner 2 Elevation" editType="number" onEdit={setEditing} />
                <Field label="Elevation — Low Side Corner 1" value={f['Lot Low Corner 1 Elevation']} editField="Lot Low Corner 1 Elevation" editType="number" onEdit={setEditing} />
                <Field label="Elevation — Low Side Corner 2" value={f['Lot Low Corner 2 Elevation']} editField="Lot Low Corner 2 Elevation" editType="number" onEdit={setEditing} />
                <Field label="Elev. at Midpoint of Front Curb / CL" value={f['Height at Midpoint of Front Curb or CL of Travelled Way']} editField="Height at Midpoint of Front Curb or CL of Travelled Way" editType="number" onEdit={setEditing} />
                <Field label="Elev. at Natural Grade 40' in" value={f['Height of Natural Grade 40 Feet in from Midpoint of Front Lot Line']} editField="Height of Natural Grade 40 Feet in from Midpoint of Front Lot Line" editType="number" onEdit={setEditing} />
                <Field label="Lot Slope" value={showPct(f['Lot Slope'])} derived />
                <Field label="Site Slope Configuration" value={f['LB Site Slope Configuration']} editField="LB Site Slope Configuration" onEdit={setEditing} />
                <Field label="Front Curb" value={f['Front Curb']} editField="Front Curb" onEdit={setEditing} />
                <Field label="Existing Building Height from Grade FF" value={f['Existing Height from Grade FF']} editField="Existing Height from Grade FF" editType="number" onEdit={setEditing} />
                <Field label="Existing Building Height from Slope Datum" value={f['Existing Height from Slope Datum']} editField="Existing Height from Slope Datum" editType="number" onEdit={setEditing} />
                <Field label="Existing Height Approximate" value={f['Existing Height Approximate']} editField="Existing Height Approximate" editType="checkbox" onEdit={setEditing} />
                <Field label="Existing Lot Coverage SF" value={f['Existing Lot Coverage SF']} editField="Existing Lot Coverage SF" editType="number" onEdit={setEditing} />
                <Field label="Existing Lot Coverage %" value={showPct(f['Existing Lot Coverage'])} derived />
                <Field label="Allowable Lot Coverage %" value={showPct(f['Allowable Lot Coverage Percent'], true)} derived />
                <Field label="Allowable Lot Coverage SF" value={f['Allowable Lot Coverage SF']} derived />
                <Field label="Required Landscaped Open Space % (R-1)" value={showPct(f['LB R One Required Landscaped Open Space Percent'])} derived />
                <Field label="Required Landscaped Open Space SF (R-1)" value={f['LB R One Required Landscaped Open Space SF']} derived />
                <Field label="Existing Front Yard Setback" value={f['Existing Front Yard Setback']} editField="Existing Front Yard Setback" editType="number" onEdit={setEditing} />
                <Field label="Existing Rear Yard Setback" value={f['Existing Rear Yard Setback']} editField="Existing Rear Yard Setback" editType="number" onEdit={setEditing} />
                <Field label="Existing Side Yard Setback 1" value={f['Existing Side Yard Setback 1']} editField="Existing Side Yard Setback 1" editType="number" onEdit={setEditing} />
                <Field label="Existing Side Yard Setback 2" value={f['Existing Side Yard Setback 2']} editField="Existing Side Yard Setback 2" editType="number" onEdit={setEditing} />
                <Field label="Existing Setbacks Approximate" value={f['Existing Setbacks Approximate']} editField="Existing Setbacks Approximate" editType="checkbox" onEdit={setEditing} />
                <Field label="Existing Covered Parking Spaces" value={f['Existing Covered Parking Spaces']} editField="Existing Covered Parking Spaces" editType="number" onEdit={setEditing} />
                <Field label="Existing Uncovered Parking Spaces" value={f['Existing Uncovered Parking Spaces']} editField="Existing Uncovered Parking Spaces" editType="number" onEdit={setEditing} />
              </Sub>
            )}

            <Sub collapseKey={k('approvals')} label="Existing Approvals" actions={[
              { label: 'Add approval', run: () => window.dispatchEvent(new CustomEvent('open-historical-permit-modal', { detail: { parcelId } })) },
              inTool('approvals'),
            ]}>
              {permits == null && <div className="wf-loading">Loading approvals…</div>}
              {permits && permits.length === 0 && <div className="wf-loading" style={{ color: '#7a9aaa' }}>No historical permits</div>}
              {permits && permits.map(p => (
                <div className="wf-node wf-node--item" key={p.id}><div className="wf-row">
                  <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
                  <span className="wf-bullet wf-bullet--item"></span>
                  <div className="wf-text-wrap wf-pd-field">
                    <span className="wf-pd-label">{fmtDate(p.fields['Permit Date'])}</span>
                    <span className="wf-pd-value">{p.fields['Permit Name'] || '—'}{p.fields['Reference Number'] ? ` · #${p.fields['Reference Number']}` : ''}</span>
                    {p.fields['Design Review'] && <span className="wf-status-pill">Design Review</span>}
                  </div>
                </div></div>
              ))}
            </Sub>

            <Sub collapseKey={k('surveys')} label="Surveys" meta={surveyDocs ? surveyDocs.length : null} actions={projectId ? [
              { label: 'Upload survey', run: () => window.dispatchEvent(new CustomEvent('open-reference-doc-modal', { detail: { projectId, factId: (refDocs && (refDocs.facts || []).find(x => x.show_on_parcel) || {}).id || null } })) },
            ] : null}>
              {surveyDocs == null && <div className="wf-loading">Loading surveys…</div>}
              {surveyDocs && surveyDocs.length === 0 && <div className="wf-loading" style={{ color: '#7a9aaa' }}>No survey uploaded. Upload it here or from a milestone's Kit.</div>}
              {surveyDocs && surveyDocs.map(d => (
                <div className="wf-node wf-node--item" key={d.id} data-key={'parcel-refdoc:' + d.id}><div className="wf-row">
                  <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
                  <span className="wf-bullet wf-bullet--item"></span>
                  <div className="wf-text-wrap wf-pd-field">
                    <span className="wf-pd-label">{fmtDate(d.created_at)}</span>
                    <span className="wf-pd-value" style={{ cursor: 'pointer', color: 'var(--aj-accent-blue)' }} title="Open"
                      onClick={() => openReferenceDocument(d.id).catch(() => {})}>{d.name}</span>
                  </div>
                </div></div>
              ))}
            </Sub>

            <Sub collapseKey={k('jurisdiction')} label="Jurisdiction & Agencies">
              <Field label="Jurisdiction" value={jurisName} tool={inTool('zoning')} />
              <Field label="Type" value={juris['Type']} tool={inPermitting} />
              <Field label="Municipal Code" value={codeUrl} link tool={inPermitting} />
              <Field label="GIS Parcel Search" value={gisUrl} link tool={inPermitting} />
              {DEPTS.map(([key, base]) => {
                const name = juris[base]
                if (!has(name)) return null
                return (
                  <Sub key={key} collapseKey={k('dept:' + key)} label={`${key} — ${show(name)}`}>
                    <Field label="Phone" value={juris[`${base} Phone`]} />
                    <Field label="Email" value={juris[`${base} General Email`]} />
                    <Field label="Submission Method" value={juris[`${base} Submission Method`]} />
                    <Field label="Review Turnaround (days)" value={juris[`${base} Review Turnaround In Days`]} />
                    <Field label="Plan Check Expediting" value={juris[`${base} Plan Check Expediting`]} />
                    <Field label="Address" value={[juris[`${base} Street Address`], juris[`${base} City`], juris[`${base} State`], juris[`${base} Zip`]].filter(has).join(', ')} />
                  </Sub>
                )
              })}
              <Sub
                collapseKey={k('agencies')}
                label="Agencies"
                meta={Array.isArray(agencies) ? agencies.length : null}
                zoomKey={'parcel-detail:' + parcelId + ':agencies'}
              >
                {agencies == null && <div className="wf-loading">Loading agencies…</div>}
                {Array.isArray(agencies) && agencies.length === 0 && <div className="wf-loading" style={{ color: '#7a9aaa' }}>No other agencies</div>}
                {Array.isArray(agencies) && agencies.map(o => (
                  <Field key={o.id} label={o.fields['Organization Name'] || 'Agency'}
                    value={[o.fields['Agency Type'], o.fields['Phone'], o.fields['General Email']].filter(has).join(' · ')} />
                ))}
              </Sub>
            </Sub>
          </div>
        )}
      </div>

      {editing && (
        <ParcelFieldEditModal
          editing={editing}
          onSave={handleSaveField}
          onClose={() => setEditing(null)}
        />
      )}

    </div>
  )
}
